import axios from 'axios';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  API_LOGIN,
  API_ME_FEATURES,
  API_ME_OPERATIONS,
  API_PROFILE,
  API_USER_ROLES,
} from '../api/apiConfig';
import { AccessRule, LoginPayload, User } from './types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (payload: LoginPayload) => Promise<User>;
  logout: () => void;
  isAuthorized: (rule?: AccessRule) => boolean;
}

interface TokenResponse {
  access: string;
  refresh?: string;
}

interface ProfileResponse {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  groups?: string[];
}

interface OperationResponse {
  id: string;
  code: string;
  name: string;
}

interface FeatureResponse {
  permission_codes?: string[];
  operation_codes?: string[];
}

interface UserRoleResponse {
  role?: number;
  role_name?: string;
  operation?: string;
  operation_name?: string;
}

interface AccessSnapshot {
  operations: string[];
  permissionCodes: string[];
  roles: string[];
}

const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const isDevelopment = process.env.NODE_ENV === 'development';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const logDebug = (...args: unknown[]) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

const logError = (...args: unknown[]) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

const storeTokens = (access: string, refresh?: string) => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);

  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  sessionStorage.setItem(ACCESS_TOKEN_KEY, access);
  if (refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }

  axios.defaults.headers.common.Authorization = `Bearer ${access}`;
};

const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  delete axios.defaults.headers.common.Authorization;
};

const getStoredToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

const normalizeList = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (data && typeof data === 'object') {
    const results = (data as { results?: T[] }).results;
    if (Array.isArray(results)) {
      return results;
    }
  }
  return [];
};

const uniqueList = (items: string[]) =>
  Array.from(new Set(items.filter((item) => item && item.trim().length > 0)));

const normalizeUser = (profile: ProfileResponse): User => {
  const fullName = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`
    .replace(/\s+/g, ' ')
    .trim();
  const groups = (profile.groups ?? []).filter(
    (group): group is string => typeof group === 'string' && group.trim().length > 0,
  );

  return {
    id: String(profile.id),
    name: fullName || profile.username || profile.email || 'Usuario',
    email: profile.email || profile.username || '',
    roles: uniqueList(groups),
    operations: [],
    permissionCodes: [],
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (token?: string) => {
    const activeToken = token ?? getStoredToken();

    if (!activeToken) {
      logError('[AUTH] profile request skipped: token not found');
      throw new Error('TOKEN_NOT_FOUND');
    }

    logDebug('[AUTH] profile request');
    const { data } = await axios.get<ProfileResponse>(API_PROFILE, {
      headers: {
        Authorization: `Bearer ${activeToken}`,
      },
    });

    logDebug('[AUTH] profile response', {
      id: data.id,
      username: data.username,
      email: data.email,
      groups: data.groups,
    });
    return normalizeUser(data);
  }, []);

  const fetchAccess = useCallback(async (userId: string, token?: string): Promise<AccessSnapshot> => {
    const activeToken = token ?? getStoredToken();

    if (!activeToken) {
      throw new Error('TOKEN_NOT_FOUND');
    }

    const headers = {
      Authorization: `Bearer ${activeToken}`,
    };

    const [operationsResponse, featuresResponse, rolesResponse] = await Promise.all([
      axios.get<OperationResponse[]>(API_ME_OPERATIONS, { headers }),
      axios.get<FeatureResponse[]>(API_ME_FEATURES, { headers }),
      axios
        .get(API_USER_ROLES, { headers, params: { user: userId, is_active: true } })
        .catch(() => null),
    ]);

    const operations = normalizeList<OperationResponse>(operationsResponse.data)
      .map((item) => item.code)
      .filter(Boolean);

    const features = normalizeList<FeatureResponse>(featuresResponse.data);
    const featureOperations = features.flatMap((item) => item.operation_codes ?? []);
    const permissionCodes = features.flatMap((item) => item.permission_codes ?? []);

    const roleLabels = rolesResponse
      ? normalizeList<UserRoleResponse>(rolesResponse.data)
          .map((item) => {
            const roleName = item.role_name ?? '';
            const operationName = item.operation_name ?? '';
            if (!roleName) {
              return '';
            }
            return operationName ? `${roleName} (${operationName})` : roleName;
          })
          .filter(Boolean)
      : [];

    return {
      operations: uniqueList([...operations, ...featureOperations]),
      permissionCodes: uniqueList(permissionCodes),
      roles: uniqueList(roleLabels),
    };
  }, []);

  const mergeUserAccess = useCallback((baseUser: User, access: AccessSnapshot) => {
    return {
      ...baseUser,
      operations: access.operations,
      permissionCodes: access.permissionCodes,
      roles: uniqueList([...baseUser.roles, ...access.roles]),
    };
  }, []);

  const login = useCallback(
    async ({ username, password }: LoginPayload) => {
      logDebug('[AUTH] login request', { username, password });
      logDebug('[AUTH] login target', API_LOGIN);
      clearTokens();
      try {
        const { data } = await axios.post<TokenResponse>(API_LOGIN, {
          username,
          password,
        });

        logDebug('[AUTH] login response', {
          hasAccess: Boolean(data.access),
          hasRefresh: Boolean(data.refresh),
        });

        if (!data.access) {
          throw new Error('ACCESS_TOKEN_MISSING');
        }

        storeTokens(data.access, data.refresh);
        const profile = await fetchProfile(data.access);
        let access: AccessSnapshot = { operations: [], permissionCodes: [], roles: [] };
        try {
          access = await fetchAccess(profile.id, data.access);
        } catch (accessError) {
          logError('[AUTH] access fetch failed', accessError);
        }
        const merged = mergeUserAccess(profile, access);
        setUser(merged);
        return merged;
      } catch (error) {
        logError('[AUTH] login failed', error);
        clearTokens();
        throw error;
      }
    },
    [fetchAccess, fetchProfile, mergeUserAccess],
  );

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  useEffect(() => {
    const token = getStoredToken();

    if (!token) {
      setLoading(false);
      return;
    }

    fetchProfile(token)
      .then(async (profile) => {
        try {
          const access = await fetchAccess(profile.id, token);
          setUser(mergeUserAccess(profile, access));
        } catch (error) {
          logError('[AUTH] access load failed', error);
          setUser(profile);
        }
      })
      .catch(() => {
        clearTokens();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [fetchAccess, fetchProfile, mergeUserAccess]);

  const isAuthorized = useCallback(
    (rule?: AccessRule) => {
      if (!rule || (!rule.operations?.length && !rule.permissions?.length)) {
        return Boolean(user);
      }

      if (!user) {
        return false;
      }

      const fullAccess = user.permissionCodes.includes('full_access');
      const hasOperations =
        !rule.operations?.length ||
        rule.operations.some((operation) => user.operations.includes(operation));
      const hasPermissions =
        fullAccess ||
        !rule.permissions?.length ||
        rule.permissions.some((permission) => user.permissionCodes.includes(permission));

      return hasOperations && hasPermissions;
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      logout,
      isAuthorized,
    }),
    [user, loading, login, logout, isAuthorized],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
